import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { ArrowRight, BarChart3, Briefcase, CheckCircle2, Megaphone, Target, Users } from "lucide-react";
import { usePublicConfig } from "@/lib/usePublicConfig";

function fmt(cents) {
  return `€${(cents / 100).toFixed(2).replace(".", ",")}`;
}

const HERO_IMG =
  "https://images.unsplash.com/photo-1695388474402-ed805a890d8d?crop=entropy&cs=srgb&fm=jpg&w=900&q=85";

export default function VoorBedrijven() {
  const cfg = usePublicConfig();
  const b2b = cfg?.pricing?.b2b_monthly_cents || 2900;

  return (
    <div data-testid="b2b-landing-page">
      <Seo path="/voor-bedrijven" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute top-20 -left-20 w-[420px] h-[420px] mesh-blob bg-[#264653]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-border text-xs font-semibold text-ink-soft">
              <Briefcase className="h-3.5 w-3.5 text-[#264653]" /> CheckExpert voor bedrijven
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-ink mt-6 leading-[1.05]">
              Bereik <span className="text-[#264653]">duizenden</span> mensen op het juiste moment.
            </h1>
            <p className="text-lg text-ink-soft mt-6 max-w-xl leading-relaxed">
              CheckExpert-gebruikers krijgen, na hun analyse, een lijst met aanbevolen experten in hun regio: advocaten, energieadviseurs, vastgoedmakelaars, makelaars in verzekeringen. Word jij die expert?
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/voor-bedrijven/aanmelden"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#264653] hover:bg-[#1F3A45] text-white font-semibold shadow-lg shadow-[#264653]/20 transition-colors"
                data-testid="b2b-hero-cta"
              >
                Meld je bedrijf aan — {fmt(b2b)}/maand <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-border hover:border-ink-soft text-ink font-semibold transition-colors"
              >
                Vraag een demo
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-ink-soft">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#10B981]" /> Opzegbaar per maand</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#10B981]" /> Eerlijke matching</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#264653]/20 to-[#10B981]/20 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl border border-white/60 bg-white/70 backdrop-blur-xl p-3 shadow-2xl">
                <img src={HERO_IMG} alt="" className="w-full h-[420px] rounded-2xl object-cover" />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl border border-border shadow-lg p-4">
                  <div className="text-xs text-ink-soft mb-1">Maandelijkse leads</div>
                  <div className="font-display text-3xl font-bold text-ink">3–12</div>
                  <div className="text-xs text-ink-soft mt-1">in jouw regio</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-end mb-12">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#264653]">Zo werkt het</span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-ink mt-3 tracking-tight">
                Eenvoudig, eerlijk, transparant.
              </h2>
            </div>
            <p className="text-ink-soft text-lg max-w-md">
              Geen veiling, geen pay-per-click. Je betaalt een vast bedrag per maand voor zichtbaarheid bij gekwalificeerde leads in jouw branche en regio.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Meld je aan", text: "Vul je bedrijfsgegevens in: branche, regio's, expertise. We checken of je past binnen onze kwaliteitsnormen.", Icon: Briefcase },
              { step: "02", title: "Verschijn in aanbevelingen", text: "Gebruikers die een check doen in jouw expertisegebied krijgen jouw bedrijf voorgesteld — in de bevestiging-e-mail of in het rapport.", Icon: Target },
              { step: "03", title: "Ontvang gekwalificeerde leads", text: "Mensen die jouw expertise nodig hebben, vinden je direct. Geen verspilde marketingbudgetten.", Icon: BarChart3 },
            ].map(({ step, title, text, Icon }) => (
              <div key={step} className="rounded-2xl bg-secondary border border-border p-8">
                <div className="font-display text-5xl font-black text-[#264653]/15">{step}</div>
                <Icon className="h-6 w-6 text-[#264653] -mt-8" />
                <h3 className="font-display text-xl font-semibold text-ink mt-5">{title}</h3>
                <p className="text-sm text-ink-soft mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#264653]">Voor wie</span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-ink mt-3 tracking-tight">
              Geschikt voor experts in deze sectoren.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: "Advocaten", text: "Familierecht · Huurrecht · Arbeidsrecht · Vastgoed" },
              { title: "Boekhouders", text: "KMO's · zelfstandigen · loon" },
              { title: "Verzekeringsmakelaars", text: "Onafhankelijke makelaars in BE & NL" },
              { title: "Energieadviseurs", text: "EPC · audits · zonnepanelen" },
              { title: "Vastgoedmakelaars", text: "Aan- en verkoop · bouwtechnisch advies" },
              { title: "Notarissen", text: "Onroerend goed · scheidingen" },
              { title: "Hypotheekadviseurs", text: "Vergelijken · ondersteuning" },
              { title: "Kredietbemiddelaars", text: "Erkend · onafhankelijk advies" },
            ].map((s) => (
              <div key={s.title} className="rounded-2xl bg-white border border-border p-6">
                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-[#264653]">
                  <Users className="h-5 w-5" />
                </div>
                <h3 className="font-display text-base font-semibold text-ink mt-4">{s.title}</h3>
                <p className="text-xs text-ink-soft mt-1">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Megaphone className="h-10 w-10 text-blue-300 mx-auto" />
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-4">
            {fmt(b2b)} per maand. <span className="text-blue-300">Punt.</span>
          </h2>
          <p className="text-slate-300 mt-4 leading-relaxed">
            Geen verbinding voor 12 maanden. Geen verborgen kosten. Geen pay-per-click waar je budget in 1 dag op gaat.
          </p>
          <ul className="mt-8 space-y-3 text-left max-w-sm mx-auto">
            {["Vermelding in aanbevelingen aan gebruikers","Vermelding op /voor-bedrijven/{slug} profielpagina","Targeting per branche en regio","Maandelijks rapport met je zichtbaarheid","Opzegbaar per maand, zonder uitleg"].map((t) => (
              <li key={t} className="flex items-start gap-2 text-slate-200">
                <CheckCircle2 className="h-5 w-5 text-blue-300 mt-0.5" /><span>{t}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/voor-bedrijven/aanmelden"
            className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-blue-400 hover:bg-blue-300 text-slate-900 font-bold transition-colors"
            data-testid="b2b-bottom-cta"
          >
            Meld je bedrijf aan <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="text-xs text-slate-400 mt-4">Aanvragen worden binnen 2 werkdagen behandeld.</p>
        </div>
      </section>
    </div>
  );
}
